<script setup lang="ts">
  defineOptions({
    name: 'ChatContainer',
  })

  /**
   * ChatContainer - Main container for all chat UI components
   *
   * This component encapsulates all chat-related UI:
   * - Chat panel (conversation list)
   * - Active chat windows
   * - All event handlers for chat interactions
   *
   * Usage: Simply render <ChatContainer /> in your app's root layout.
   * The chat panel can be toggled via useChats().store.togglePanel()
   */
  import { computed, onMounted, ref } from 'vue'

  import { useGlobal } from '@common/core-layer/composables/useGlobal'

  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import ChatPanel from './ChatPanel.vue'

  import ChatWindow from './ChatWindow.vue'

  import { useChats } from '../../composables/useChats'

  import { useChatsRealtime } from '../../composables/useChatsRealtime'

  const props = defineProps({
    groupSlug: {
      type: String,
      default: undefined,
    },
    width: {
      type: Number,
      default: 380,
    },
  })

  const chats = useChats()

  const { CPNavigateTo } = useCPNavigation()

  const { isMobileOrTablet } = useGlobal()

  const isMobile = ref(false)

  const isChatPanelVisible = computed(() => {
    return !isMobile.value && (chats.store.isPanelOpen || chats.store.isPanelMinimized)
  })

  const isChatPanelMinimized = computed(() => {
    return chats.store.isPanelMinimized
  })

  const activeChatWindows = computed(() => {
    return isMobile.value ? [] : chats.store.activeChatWindows
  })

  const handleChatClick = async () => {
    if (isMobile.value) {
      try {
        // Chats are global on mobile; open the conversations route directly.
        await CPNavigateTo({
          name: 'communities-conversations',
        })
      } catch (error) {
        console.error('Failed to navigate to conversations, redirecting to home:', error)
        await CPNavigateTo({ name: 'index' })
      }
      return
    }

    chats.store.togglePanel()
  }

  const handleChatPanelClose = () => {
    chats.store.closePanel()
  }

  const handleChatWindowToggleMinimize = (conversationId: string) => {
    chats.store.toggleChatWindowMinimize(conversationId)
  }

  const handleChatWindowClose = (conversationId: string) => {
    chats.store.closeChatWindow(conversationId)
  }

  onMounted(() => {
    isMobile.value = isMobileOrTablet()
  })

  useChatsRealtime()

  defineExpose({
    handleChatClick,
  })
</script>

<template>
  <!-- Chat Container - Fixed position at bottom right -->
  <div class="fixed bottom-0 right-4 z-[10010] flex items-end gap-2">
    <!-- Active Chat Windows (positioned to the left of the panel) -->
    <TransitionGroup
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-full scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-full scale-95"
    >
      <div
        v-for="chatWindow in activeChatWindows"
        :key="chatWindow.conversationId"
        class="transition-all duration-300"
        :style="{ isolation: 'isolate', overscrollBehavior: 'contain' }"
      >
        <ChatWindow
          :conversation="chatWindow.conversation"
          :is-minimized="chatWindow.isMinimized"
          @toggle-minimize="handleChatWindowToggleMinimize(chatWindow.conversationId)"
          @close="handleChatWindowClose(chatWindow.conversationId)"
        />
      </div>
    </TransitionGroup>

    <!-- Chat Panel (Conversation List) - rightmost -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-full"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-full"
    >
      <div
        v-if="isChatPanelVisible"
        class="transition-all duration-300"
        :class="[isChatPanelMinimized ? 'h-auto' : 'h-[calc(100vh-80px)]']"
        :style="{ isolation: 'isolate', overscrollBehavior: 'contain' }"
      >
        <ChatPanel :width="props.width" @close="handleChatPanelClose" />
      </div>
    </Transition>
  </div>
</template>
